import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Page publique « Postuler » — erpia.ch/postuler/:token
 *
 * Le candidat arrive d'une offre LinkedIn, d'un post, d'un message ou d'un
 * site d'emploi ; il n'a pas de compte et le fait souvent depuis son
 * téléphone. Une colonne : l'offre, puis la candidature — identité, CV,
 * quelques questions, consentement. Les questions se répondent en touchant
 * une ligne entière (pas de liste déroulante sur mobile) : c'est l'élément
 * signature de la page, et la seule chose qui demande un choix.
 *
 * Ce que la page ne sait pas, et c'est voulu : la réponse attendue. Le
 * serveur juge la candidature à l'arrivée ; le candidat lit la même phrase,
 * retenu ou non.
 *
 * Routes SANS auth :
 *   GET  /public/annonce/:token → { titre, lieu, texte, organisation, cv_obligatoire, questions, confidentialite }
 *                                 404 lien inconnu · 410 annonce fermée
 *   POST /public/annonce/:token?source= (multipart : prenom, nom, email,
 *        telephone, linkedin, cv, reponses (JSON), consentement, site_web)
 *        → { message } · 400 + champ · 409 déjà postulé · 410 fermée · 429
 */
const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon }) => <i className={`bi bi-${icon}`} aria-hidden="true" />;

// Le texte de l'annonce : des paragraphes, et des listes « • ».
function Texte({ texte }) {
  const blocs = String(texte || '').split(/\n{2,}/).map(b => b.split('\n').map(l => l.trim()).filter(Boolean)).filter(b => b.length);
  return (
    <div className="pubann-texte">
      {blocs.map((lignes, i) => {
        const puces = lignes.filter(l => /^[•\-–]\s?/.test(l));
        if (!puces.length) return <p key={i}>{lignes.join(' ')}</p>;
        const titre = /^[•\-–]\s?/.test(lignes[0]) ? null : lignes[0];
        return (
          <section key={i}>
            {titre && <h2>{titre}</h2>}
            <ul>{puces.map((l, j) => <li key={j}>{l.replace(/^[•\-–]\s?/, '')}</li>)}</ul>
          </section>
        );
      })}
    </div>
  );
}

export default function PublicAnnonce({ token }) {
  const [etat, setEtat] = useState('chargement'); // chargement | pret | invalide | ferme | envoye | deja
  const [data, setData] = useState(null);
  const [message, setMessage] = useState('');
  const [champs, setChamps] = useState({ prenom: '', nom: '', email: '', telephone: '', linkedin: '', site_web: '' });
  const [reponses, setReponses] = useState({});
  const [cv, setCv] = useState(null);
  const [consent, setConsent] = useState(false);
  const [erreur, setErreur] = useState('');
  const [champErreur, setChampErreur] = useState(null);
  const [busy, setBusy] = useState(false);
  const refs = useRef({});
  const source = useMemo(() => {
    try { return new URLSearchParams(window.location.search).get('source') || ''; } catch { return ''; }
  }, []);

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/public/annonce/${encodeURIComponent(token)}`);
        const j = await r.json().catch(() => ({}));
        if (annule) return;
        if (r.status === 410) { setMessage(j.error || ''); setData({ titre: j.titre }); setEtat('ferme'); return; }
        if (!r.ok || j.success === false) { setMessage(j.error || ''); setEtat('invalide'); return; }
        setData(j);
        document.title = `${j.titre} — postuler`;
        setEtat('pret');
      } catch { if (!annule) setEtat('invalide'); }
    })();
    return () => { annule = true; };
  }, [token]);

  const questions = useMemo(() => data?.questions || [], [data]);
  const manque = useMemo(() => {
    const m = [];
    if (!champs.prenom.trim()) m.push('prénom');
    if (!champs.nom.trim()) m.push('nom');
    if (!champs.email.trim()) m.push('e-mail');
    if (data?.cv_obligatoire && !cv) m.push('CV');
    const sansReponse = questions.filter(q => q.obligatoire && !String(reponses[q.id] ?? '').trim()).length;
    if (sansReponse) m.push(`${sansReponse} question${sansReponse > 1 ? 's' : ''}`);
    if (!consent) m.push('accord');
    return m;
  }, [champs, cv, questions, reponses, consent, data]);

  const fauteSur = (champ) => champErreur?.champ === champ;
  const majChamp = (k) => (e) => { const v = e.target.value; setChamps(p => ({ ...p, [k]: v })); if (fauteSur(k)) setChampErreur(null); };
  const repondre = (id, v) => { setReponses(p => ({ ...p, [id]: v })); if (fauteSur(`q_${id}`)) setChampErreur(null); };

  const envoyer = async (e) => {
    e.preventDefault();
    setBusy(true); setErreur(''); setChampErreur(null);
    try {
      const f = new FormData();
      Object.entries(champs).forEach(([k, v]) => f.append(k, String(v || '').trim()));
      f.append('reponses', JSON.stringify(reponses));
      f.append('consentement', consent ? 'true' : 'false');
      if (cv) f.append('cv', cv, cv.name);
      const r = await fetch(`${BASE}/public/annonce/${encodeURIComponent(token)}${source ? `?source=${encodeURIComponent(source)}` : ''}`, { method: 'POST', body: f });
      const j = await r.json().catch(() => ({}));
      if (r.status === 400 && j.champ) {
        setChampErreur({ champ: j.champ, message: j.error });
        const el = refs.current[j.champ];
        if (el) { el.focus(); el.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
        return;
      }
      if (r.status === 409) { setMessage(j.error || ''); setEtat('deja'); return; }
      if (r.status === 410) { setMessage(j.error || ''); setEtat('ferme'); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || 'L\'envoi n\'a pas abouti — réessayez dans un instant.');
      setMessage(j.message || ''); setEtat('envoye');
      window.scrollTo({ top: 0 });
    } catch (err) { setErreur(err.message); }
    finally { setBusy(false); }
  };

  const cadre = (icone, titre, texte) => (
    <main className="pubform">
      <div className="pubform-carte pubform-carte--message">
        <BI icon={icone} />
        <h1>{titre}</h1>
        <p>{texte}</p>
      </div>
    </main>
  );
  if (etat === 'chargement') return cadre('arrow-repeat', 'Ouverture de l\'annonce…', 'Un instant.');
  if (etat === 'invalide') return cadre('link-45deg', 'Cette annonce est introuvable', message || 'Son lien a peut-être changé. Demandez-le à la personne qui vous l\'a transmis.');
  if (etat === 'ferme') return cadre('pause-circle', data?.titre ? `« ${data.titre} » est close` : 'Cette annonce est close', message || 'Elle n\'accepte plus de candidatures.');
  if (etat === 'deja') return cadre('check-circle', 'Vous avez déjà postulé', message);
  if (etat === 'envoye') return cadre('check-circle-fill', 'Candidature envoyée', message);

  const faute = (champ) => fauteSur(champ) && <span className="pubform-champ-erreur" id={`e-${champ}`} role="alert">{champErreur.message}</span>;
  const attrs = (champ) => ({ ref: (el) => { refs.current[champ] = el; }, 'aria-invalid': fauteSur(champ) || undefined, 'aria-describedby': fauteSur(champ) ? `e-${champ}` : undefined, className: fauteSur(champ) ? 'is-faux' : undefined });

  return (
    <main className="pubform">
      <article className="pubform-carte">
        <header className="pubform-tete">
          {data.organisation && <p className="pubform-kicker">{data.organisation} recrute</p>}
          <h1>{data.titre}</h1>
          {data.lieu && <p className="pubann-lieu"><BI icon="geo-alt" /> {data.lieu}</p>}
        </header>
        <Texte texte={data.texte} />
      </article>

      <form className="pubform-carte pubann-form" onSubmit={envoyer} noValidate>
        <header className="pubform-tete">
          <h2 className="pubann-h2">Postuler</h2>
          <p>Quelques minutes. Les champs sans mention sont nécessaires.</p>
        </header>
        {erreur && <div className="pubform-erreur" role="alert">{erreur}</div>}

        <div className="pubform-champs">
          <div className="pubann-duo">
            <label className="pubform-champ"><span className="pubform-label">Prénom</span>
              <input {...attrs('prenom')} value={champs.prenom} onChange={majChamp('prenom')} autoComplete="given-name" />{faute('prenom')}
            </label>
            <label className="pubform-champ"><span className="pubform-label">Nom</span>
              <input {...attrs('nom')} value={champs.nom} onChange={majChamp('nom')} autoComplete="family-name" />{faute('nom')}
            </label>
          </div>
          <label className="pubform-champ"><span className="pubform-label">E-mail</span>
            <input {...attrs('email')} type="email" value={champs.email} onChange={majChamp('email')} autoComplete="email" />{faute('email')}
          </label>
          <label className="pubform-champ"><span className="pubform-label">Téléphone <em>facultatif</em></span>
            <input {...attrs('telephone')} type="tel" value={champs.telephone} onChange={majChamp('telephone')} autoComplete="tel" />{faute('telephone')}
          </label>
          <label className="pubform-champ"><span className="pubform-label">Profil LinkedIn <em>facultatif</em></span>
            <input {...attrs('linkedin')} type="url" value={champs.linkedin} onChange={majChamp('linkedin')} placeholder="https://www.linkedin.com/in/…" />{faute('linkedin')}
          </label>
          <div className="pubform-champ">
            <span className="pubform-label">CV {!data.cv_obligatoire && <em>facultatif</em>}</span>
            <label className={`pubann-fichier${cv ? ' is-plein' : ''}${fauteSur('cv') ? ' is-faux' : ''}`}>
              <BI icon={cv ? 'file-earmark-check' : 'file-earmark-arrow-up'} />
              <span>{cv ? cv.name : 'Choisir un fichier — PDF ou Word, 8 Mo au plus'}</span>
              <input ref={(el) => { refs.current.cv = el; }} type="file" accept=".pdf,.doc,.docx,.odt,.rtf"
                onChange={e => { setCv(e.target.files?.[0] || null); if (fauteSur('cv')) setChampErreur(null); }} />
            </label>
            {faute('cv')}
          </div>

          {questions.map(q => {
            const id = `q_${q.id}`;
            if (q.saisie === 'choix' || q.saisie === 'oui_non') {
              return (
                <fieldset key={q.id} className="pubann-question" aria-describedby={fauteSur(id) ? `e-${id}` : undefined}>
                  <legend className="pubform-label">{q.question}</legend>
                  <div className={`pubann-options${q.saisie === 'oui_non' ? ' pubann-options--duo' : ''}`}>
                    {(q.choix || []).map((c, i) => (
                      <label key={c.cle} className={`pubann-option${reponses[q.id] === c.cle ? ' is-on' : ''}`}>
                        <input type="radio" name={id} value={c.cle} checked={reponses[q.id] === c.cle} onChange={() => repondre(q.id, c.cle)}
                          ref={i === 0 ? (el) => { refs.current[id] = el; } : undefined} />
                        <span>{c.libelle}</span>
                      </label>
                    ))}
                  </div>
                  {faute(id)}
                </fieldset>
              );
            }
            return (
              <label key={q.id} className="pubform-champ"><span className="pubform-label">{q.question}{!q.obligatoire && <em>facultatif</em>}</span>
                {q.saisie === 'nombre'
                  ? <input {...attrs(id)} type="number" inputMode="numeric" min={q.min ?? 0} max={q.max ?? 60} value={reponses[q.id] ?? ''} onChange={e => repondre(q.id, e.target.value)} className={`pubform-mono${fauteSur(id) ? ' is-faux' : ''}`} />
                  : <textarea {...attrs(id)} rows={3} maxLength={q.max || 500} value={reponses[q.id] ?? ''} onChange={e => repondre(q.id, e.target.value)} />}
                {faute(id)}
              </label>
            );
          })}

          {/* Piège à robots : invisible pour une personne, rempli par un robot. */}
          <label className="pubann-piege" aria-hidden="true">Site web
            <input tabIndex={-1} autoComplete="off" value={champs.site_web} onChange={majChamp('site_web')} />
          </label>

          <label className={`pubann-accord${fauteSur('consentement') ? ' is-faux' : ''}`}>
            <input ref={(el) => { refs.current.consentement = el; }} type="checkbox" checked={consent} onChange={e => { setConsent(e.target.checked); if (fauteSur('consentement')) setChampErreur(null); }} />
            <span>{data.confidentialite}</span>
          </label>
          {faute('consentement')}
        </div>

        <div className="pubform-pied">
          <span className="pubform-reste">{manque.length ? `Il reste : ${manque.join(', ')}.` : 'Tout est prêt.'}</span>
          <button type="submit" className="pubform-envoyer" disabled={busy}>{busy ? 'Envoi…' : 'Envoyer ma candidature'}</button>
        </div>
      </form>
    </main>
  );
}
